import React from "react";
import { Link } from "react-router-dom";
import moment from "jalali-moment";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import InboxIcon from "@mui/icons-material/Inbox";
import {
    SectionCard,
    EmptyState,
    Skeleton,
} from "../../../shares/dashboard/DashboardWidgets";

/**
 * Upcoming assignment deadlines, most urgent first.
 *
 * Urgency is encoded twice on purpose - a coloured rail and a worded label -
 * so it does not rely on colour alone (WCAG 1.4.1).
 */

const URGENCY = {
    overdue: { rail: "bg-danger", text: "text-danger", label: "مهلت گذشته" },
    today: { rail: "bg-warning", text: "text-warning", label: "امروز" },
    soon: { rail: "bg-primary", text: "text-primary", label: "نزدیک" },
    later: { rail: "bg-rule-interactive", text: "text-ink-subtle", label: "" },
};

const describeDays = (days) => {
    if (days === null || days === undefined) return "";
    if (days < 0) return `${Math.abs(days).toLocaleString("fa-IR")} روز گذشته`;
    if (days === 0) return "امروز";
    if (days === 1) return "فردا";
    return `${days.toLocaleString("fa-IR")} روز دیگر`;
};

export default function UpcomingList({ deadlines, loading, index = 0 }) {
    return (
        <SectionCard
            title="مهلت‌های پیش رو"
            description="تکالیفی که باید تحویل بدی"
            icon={PendingActionsIcon}
            actionLabel="همه تکالیف"
            actionTo="/dashboard/homework"
            index={index}
        >
            {loading && deadlines.length === 0 ? (
                <div className="flex flex-col gap-sm">
                    {[0, 1, 2].map((key) => (
                        <Skeleton key={key} className="h-[6.4rem] w-full" />
                    ))}
                </div>
            ) : deadlines.length === 0 ? (
                <EmptyState
                    icon={InboxIcon}
                    title="تکلیف بازی نداری"
                    description="همه‌چیز تحویل شده. کارت درسته!"
                />
            ) : (
                <ul className="flex flex-col gap-xs">
                    {deadlines.slice(0, 5).map((item, position) => {
                        const tone = URGENCY[item.urgency] ?? URGENCY.later;
                        return (
                            <li key={item.id}>
                                <Link
                                    to="/dashboard/homework"
                                    className="group flex items-center gap-md rounded-card border border-rule-subtle bg-surface p-md transition-colors duration-150 hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`h-[3.2rem] w-[3px] shrink-0 rounded-pill ${tone.rail}`}
                                    />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-base text-ink">
                                            {item.title}
                                        </span>
                                        {item.subtitle && (
                                            <span className="block truncate text-sm text-ink-subtle">
                                                {item.subtitle}
                                            </span>
                                        )}
                                    </span>
                                    <span className="shrink-0 text-end">
                                        <span
                                            className={`block text-sm font-medium ${tone.text}`}
                                        >
                                            {describeDays(item.daysLeft)}
                                        </span>
                                        {item.endsAt && (
                                            <span className="block text-xs text-ink-subtle">
                                                {moment(new Date(item.endsAt))
                                                    .locale("fa")
                                                    .format("DD MMMM")}
                                            </span>
                                        )}
                                    </span>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            )}
        </SectionCard>
    );
}
